import fp from 'lodash/fp';
import { handleActions } from 'redux-actions';
import { actions } from './actions';

export const initialState = {
  user: {
    error: null,
    industryRoles: [],
    industryRolesIsFetching: false,
    isFetching: false,
    isUpdating: false,
    taskStack: [],
    setBusinessUserTrainingPlanSuccess: undefined
  }
}

export const reducerDefinition = {
  [actions.business.user.initTaskStack().type]: ({ user, ...state }, action) => ({
    ...state,
    user: {
      ...user,
      taskStack: action.payload.append ? user.taskStack.concat(action.payload.tasks) : action.payload.tasks
    }
  }),
  [actions.business.user.updateTaskStack().type]: ({ user, ...state }, action) => ({
    ...state,
    user: {
      ...user,
      taskStack: fp.flow(
        fp.map(
          (task) => task.id === action.payload.taskId ? { ...task, status: action.payload.status, data: action.payload.data } : task
        )
      )(user.taskStack)
    }
  }),
  [actions.business.user.api.getQualifications.request]: ({ user, ...state }, action) => ({
    ...state,
    user: {
      ...user,
      isFetching: true
    }
  }),
  [actions.business.user.api.getQualifications.success]: ({ user, ...state }, action) => ({
    ...state,
    user: {
      ...user,
      qualifications: action.payload.qualifications,
      isFetching: false
    }
  }),
  [actions.business.user.api.getQualifications.failed]: ({ user, ...state }, action) => ({
    ...state,
    user: {
      ...user,
      error: action.payload.error,
      isFetching: false
    }
  }),
  [actions.business.user.api.getProfile.success]: ({ user, ...state }, action) => ({
    ...state,
    user: {
      ...user,
      profile: action.payload.profile,
    }
  }),
  [actions.business.user.api.getSnapshotReport.success]: ({ user, ...state }, action) => ({
    ...state,
    user: {
      ...user,
      snapshotReport: action.payload.report,
    }
  }),
  [actions.business.user.api.setCpdCategorys.request]: ({ user, ...state }, action) => ({
    ...state,
    user: {
      ...user,
      setBusinessUserTrainingPlanSuccess: undefined,
      isUpdating: true
    }
  }),
  [actions.business.user.api.setCpdCategorys.success]: ({ user, teamMembers, ...state }, action) => ({
    ...state,
    user: {
      ...user,
      cpdCategorys: action.payload.cpdCategorys.cpdCategorys,
      setBusinessUserTrainingPlanSuccess: { success: true, info: 'Training Plan Updated' },
      isUpdating: false
    },
    teamMembers: fp.map(
      (teamMember) => action.payload.cpdCategorys.userId === teamMember.user.id ? { ...teamMember, cpdCategorys: fp.map(aoa => aoa.cpdCategoryDetail)(action.payload.cpdCategorys.cpdCategorys) } : teamMember
    )(teamMembers)
  }),
  [actions.business.user.api.setCpdCategorys.failed]: ({ user, ...state }, action) => ({
    ...state,
    user: {
      ...user,
      error: action.payload.error,
      setBusinessUserTrainingPlanSuccess: { success: false, info: 'Training Plan Update Failed' },
      isUpdating: false
    }
  }),
  [actions.business.user.api.getIndustryRoles.request]: ({ user, ...state }, action) => ({
    ...state,
    user: {
      ...user,
      industryRoles: initialState.user.industryRoles,
      industryRolesIsFetching: true
    }
  }),
  [actions.business.user.api.getIndustryRoles.success]: ({ user, userIndustries, ...state }, action) => ({
    ...state,
    user: {
      ...user,
      error: null,
      industryRoles: action.payload.industryRoles,
      industryRolesIsFetching: false
    },
    userIndustries: {
      ...userIndustries,
      [fp.head(action.payload.industryRoles).userId]: action.payload.industryRoles
    }
  }),
  [actions.business.user.api.getIndustryRoles.failed]: ({ user, ...state }, action) => ({
    ...state,
    user: {
      ...user,
      error: action.payload.error,
      industryRolesIsFetching: false
    }
  }),
  [actions.business.user.api.updateIndustryRole.success]: ({ user, teamMembers, ...state }, { payload: { industryRole } }) => ({
    ...state,
    user: {
      ...user,
      industryRoles: fp.concat(
        fp.filter(({ id }) => id !== industryRole.id)(state.industryRoles),
        industryRole
      )
    },
    teamMembers: fp.map(
      (teamMember) => industryRole.userId === teamMember.user.id ? { ...teamMember, industryUser: industryRole } : teamMember
    )(teamMembers)
  }),
  [actions.business.user.api.getCpdActivities.request]: ({ ...state }, action) => ({
    ...state,
    isFetchingCpdActivities: true
  }),
  [actions.business.user.api.loadMore.request]: ({ ...state }, action) => ({
    ...state,
    isFetchingCpdActivities: true
  }),
  [actions.business.user.api.getCpdActivities.success]: ({ ...state }, { payload: { cpdActivities } }) => ({
    ...state,
    cpdActivities,
    isFetchingCpdActivities: false,
    fetchCpdActivitiesUserID: cpdActivities.userCpdPoints && cpdActivities.userCpdPoints[0] && cpdActivities.userCpdPoints[0].userId
  }),
  [actions.business.user.api.loadMore.success]: ({ ...state }, { payload: { cpdActivities } }) => ({
    ...state,
    cpdActivities: {
      ...cpdActivities,
      userCpdPoints: fp.concat(state.cpdActivities.userCpdPoints, cpdActivities.userCpdPoints)
    },
    isFetchingCpdActivities: false,
    fetchCpdActivitiesUserID: cpdActivities.userCpdPoints && cpdActivities.userCpdPoints[0] && cpdActivities.userCpdPoints[0].userId
  }),
  [actions.business.user.api.getCpdCategorys.request]: ({ ...state }, action) => ({
    ...state,
    isFetchingMemberCpdCategorys: true
  }),
  [actions.business.user.api.getCpdCategorys.success]: ({ ...state }, { payload: { getCpdCategorys } }) => ({
    ...state,
    isFetchingMemberCpdCategorys: false,
    memberCpdCategorys: getCpdCategorys
  }),
  [actions.business.user.api.getExternalCpd.request]: ({ ...state }) => ({
    ...state,
    isFetchingExternalCpdForReview: true,
    externalCpdForReview: undefined,
    externalCpdError: null
  }),
  [actions.business.user.api.getExternalCpd.failed]: ({ ...state }, action) => ({
    ...state,
    isFetchingExternalCpdForReview: false,
    externalCpdError: action.payload.error
  }),
  [actions.business.user.api.getExternalCpd.success]: ({ ...state }, { payload: externalCpd }) => ({
    ...state,
    isFetchingExternalCpdForReview: false,
    externalCpdForReview: externalCpd
  }),
  [actions.business.user.api.updateExternalCpd.request]: ({ ...state }) => ({
    ...state,
    isFetchingExternalCpdForReview: true,
    externalCpdError: null
  }),
  [actions.business.user.api.updateExternalCpd.failed]: ({ ...state }, action) => ({
    ...state,
    isFetchingExternalCpdForReview: false,
    externalCpdError: action.payload.error
  }),
  [actions.business.user.api.updateExternalCpd.success]: ({ ...state }, { payload: externalCpd }) => ({
    ...state,
    isFetchingExternalCpdForReview: false,
    externalCpdForReview: externalCpd
  }),
  [actions.business.user.api.addExternalCpd.request]: ({ ...state }) => ({
    ...state,
    isFetchingExternalCpdForReview: true,
    externalCpdError: null
  }),
  [actions.business.user.api.addExternalCpd.failed]: ({ ...state }, action) => ({
    ...state,
    isFetchingExternalCpdForReview: false,
    externalCpdError: action.payload.error
  }),
  [actions.business.user.api.addExternalCpd.success]: ({ ...state }, { payload: externalCpd }) => ({
    ...state,
    isFetchingExternalCpdForReview: false,
    externalCpdForReview: externalCpd
  }),
  [actions.business.user.api.deleteExternalCpd.request]: ({ ...state }) => ({
    ...state,
    isFetchingExternalCpdForReview: true,
    externalCpdError: null
  }),
  [actions.business.user.api.deleteExternalCpd.failed]: ({ ...state }, action) => ({
    ...state,
    isFetchingExternalCpdForReview: false,
    externalCpdError: action.payload.error
  }),
  [actions.business.user.api.deleteExternalCpd.success]: ({ ...state }, { payload: externalCpd }) => ({
    ...state,
    isFetchingExternalCpdForReview: false,
    externalCpdForReview: externalCpd
  })
}

export default handleActions(reducerDefinition, { ...initialState });


// WEBPACK FOOTER //
// ./src/state/modules/business/user/reducers.js